"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pattern = exports.customValidation = exports.required = exports.wholeNumberOnly = exports.numberOnly = exports.valueLessThanEqualTo = exports.valueGreaterThanEqualTo = exports.valueBetween = exports.lengthEqualTo = exports.lengthLessThanEqualTo = exports.lengthGreaterThanEqualTo = exports.dateOnOrBefore = exports.dateOnOrAfter = exports.dateAfterField = exports.dateBeforeField = exports.dateBefore = exports.dateAfter = void 0;
const date_fns_1 = require("date-fns");
const helpers_1 = require("../helpers");
const enums_1 = require("../structs/enums");
const helpers_2 = require("../helpers");
const defaultTFunction = (arg) => arg;
/**
 * Best used as validation for datepicker()
 *
 * dateAfter("2023-01-01") === true for values "2023-01-02" and after
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param date
 * @param options
 */
function dateAfter(date, options = {}) {
    const { locale, dateTimeFormatOptions, message = `Must be after ${(0, helpers_2.formatDate)(date, locale, dateTimeFormatOptions)}.`, translationFunction = defaultTFunction, id = 'dateAfter' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.MIN_DATE,
        value: date,
        id
    };
}
exports.dateAfter = dateAfter;
/**
 * Best used as validation for datepicker()
 *
 * dateBefore("2023-01-01") === true for values "2022-12-31" and before
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param date
 * @param options
 */
function dateBefore(date, options = {}) {
    const { locale, dateTimeFormatOptions, message = `Must be before ${(0, helpers_2.formatDate)(date, locale, dateTimeFormatOptions)}.`, translationFunction = defaultTFunction, id = 'dateBefore' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.MAX_DATE,
        value: date,
        id
    };
}
exports.dateBefore = dateBefore;
function dateBeforeField(field, options = {}) {
    const { message = `Date must be before ${helpers_1.FluidFormTokens.COMPARATOR}`, translationFunction = defaultTFunction, id = 'dateOnOrAfter' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value, parentState) => {
            var _a;
            const comparatorValue = (_a = parentState === null || parentState === void 0 ? void 0 : parentState.rawValue) === null || _a === void 0 ? void 0 : _a[field];
            return !value || (0, date_fns_1.isEqual)((0, date_fns_1.parseISO)(value), (0, date_fns_1.parseISO)(comparatorValue));
        },
        id
    };
}
exports.dateBeforeField = dateBeforeField;
function dateAfterField(field, options = {}) {
    const { message = `Date must be after ${helpers_1.FluidFormTokens.COMPARATOR}`, translationFunction = defaultTFunction, id = 'dateOnOrAfter' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value, parentState) => {
            var _a;
            const comparatorValue = (_a = parentState === null || parentState === void 0 ? void 0 : parentState.rawValue) === null || _a === void 0 ? void 0 : _a[field];
            return !value || (0, date_fns_1.isEqual)((0, date_fns_1.parseISO)(value), (0, date_fns_1.parseISO)(comparatorValue));
        },
        id
    };
}
exports.dateAfterField = dateAfterField;
/**
 * Best used as validation for datepicker()
 *
 * dateOnOrAfter("2023-01-01") === true for values "2023-01-01" and after
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param date
 * @param options
 */
function dateOnOrAfter(date, options = {}) {
    const { locale, dateTimeFormatOptions, message = `Must be on or after ${(0, helpers_2.formatDate)(date, locale, dateTimeFormatOptions)}.`, translationFunction = defaultTFunction, id = 'dateOnOrAfter' } = options;
    const isoBaseDate = (0, date_fns_1.parseISO)(date);
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value) => {
            return !value || (0, date_fns_1.isAfter)((0, date_fns_1.parseISO)(value), isoBaseDate) || (0, date_fns_1.isEqual)((0, date_fns_1.parseISO)(value), isoBaseDate);
        },
        id
    };
}
exports.dateOnOrAfter = dateOnOrAfter;
/**
 * Best used as validation for datepicker()
 *
 * dateOnOrBefore("2023-01-01") === true for values "2023-01-01" and before
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param date
 * @param options
 */
function dateOnOrBefore(date, options = {}) {
    const { locale, dateTimeFormatOptions, message = `Must be on or before ${(0, helpers_2.formatDate)(date, locale, dateTimeFormatOptions)}.`, translationFunction = defaultTFunction, id = 'doneOnOrBefore' } = options;
    const isoBaseDate = (0, date_fns_1.parseISO)(date);
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value) => {
            return !value || (0, date_fns_1.isBefore)((0, date_fns_1.parseISO)(value), isoBaseDate) || (0, date_fns_1.isEqual)((0, date_fns_1.parseISO)(value), isoBaseDate);
        },
        id
    };
}
exports.dateOnOrBefore = dateOnOrBefore;
/**
 * Best used as validation for combobox(), currencyField(), inputField(), textArea()
 *
 * lengthGreaterThanEqualTo(5) === true for values: "12345", "123456", etc.
 * lengthGreaterThanEqualTo(5) === false for values: "1234", "123", etc.
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param length
 * @param options
 */
function lengthGreaterThanEqualTo(length, options = {}) {
    const { message = `Must be greater than or equal to ${length} characters.`, translationFunction = defaultTFunction, id = 'lengthGreaterThanEqualTo' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value) => !value || value.length >= length,
        id
    };
}
exports.lengthGreaterThanEqualTo = lengthGreaterThanEqualTo;
/**
 * Best used as validation for combobox(), currencyField(), inputField(), textArea()
 *
 * lengthLessThanEqualTo(5) === true for values: "12345", "123", etc.
 * lengthLessThanEqualTo(5) === false for values: "123456", "1234567", etc.
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param length
 * @param options
 */
function lengthLessThanEqualTo(length, options = {}) {
    const { message = `Must be less than or equal to ${length} characters.`, translationFunction = defaultTFunction, id = 'lengthLessThanEqualTo' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value) => !value || value.length <= length,
        id
    };
}
exports.lengthLessThanEqualTo = lengthLessThanEqualTo;
/**
 * Best used as validation for combobox(), currencyField(), inputField(), textArea()
 *
 * lengthEqualTo(5) === true for values: "12345", "abcde", etc.
 * lengthEqualTo(5) === false for values: "1234", "123456", etc.
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param length
 * @param options
 */
function lengthEqualTo(length, options = {}) {
    const { message = `Must be ${length} characters.`, translationFunction = defaultTFunction, id = 'lengthEqualTo' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value) => !value || value.length === length,
        id
    };
}
exports.lengthEqualTo = lengthEqualTo;
/**
 * Best used as validation for combobox(), currencyField(), inputField(), textArea()
 *
 * valueBetween(1, 10) === true for values: 1, "1", 5, "5", 10, "10", etc.
 * valueBetween(1, 10) === false for values: 0, "0", 11, "11", etc.
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param minValue
 * @param maxValue
 * @param options
 */
function valueBetween(minValue, maxValue, options = {}) {
    const { message = `Must be between ${minValue} and ${maxValue}.`, translationFunction = defaultTFunction, id = 'valueBetween' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value) => {
            const numberValue = Number(value);
            return (typeof value === "string" && !value) || (!isNaN(numberValue) && minValue <= numberValue && numberValue <= maxValue);
        },
        id
    };
}
exports.valueBetween = valueBetween;
/**
 * Best used as validation for combobox(), currencyField(), inputField(), textArea()
 *
 * valueGreaterThanEqualTo(5) === true for values: 5, "5", 10, "10" etc.
 * valueGreaterThanEqualTo(5) === false for values: 0, "0", 4, "4", etc.
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param minValue
 * @param options
 */
function valueGreaterThanEqualTo(minValue, options = {}) {
    const { message = `Must be greater than or equal to ${minValue}.`, translationFunction = defaultTFunction, id = 'valueGreaterThanEqualTo' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value) => {
            const numberValue = Number(value);
            return (typeof value === "string" && !value) || (!isNaN(numberValue) && numberValue >= minValue);
        },
        id
    };
}
exports.valueGreaterThanEqualTo = valueGreaterThanEqualTo;
/**
 * Best used as validation for combobox(), currencyField(), inputField(), textArea()
 *
 * valueLessThanEqualTo(5) === true for values: 0, "0", 5, "5" etc.
 * valueLessThanEqualTo(5) === false for values: 6, "6", 10, "10", etc.
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param maxValue
 * @param options
 */
function valueLessThanEqualTo(maxValue, options = {}) {
    const { message = `Must be less than or equal to ${maxValue}.`, translationFunction = defaultTFunction, id = 'valueLessThanEqualTo' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value) => {
            const numberValue = Number(value);
            return (typeof value === "string" && !value) || (!isNaN(numberValue) && numberValue <= maxValue);
        },
        id
    };
}
exports.valueLessThanEqualTo = valueLessThanEqualTo;
/**
 * Best used as validation for combobox(), currencyField(), inputField(), textArea()
 *
 * numberOnly() === true for values: "0", "10.5", "100", etc.
 * numberOnly() === false for values: "1a", "foo", etc.
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param options
 */
function numberOnly(options = {}) {
    const { message = "Must be a number.", translationFunction = defaultTFunction, id = 'numberOnly' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value) => !value || !isNaN(Number(value)),
        id
    };
}
exports.numberOnly = numberOnly;
/**
 * Best used as validation for combobox(), currencyField(), inputField(), textArea()
 *
 * wholeNumberOnly() === true for values: "0", "100", etc.
 * wholeNumberOnly() === false for values: "1a", "10.5", "foo", etc.
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param options
 */
function wholeNumberOnly(options = {}) {
    const { message = "Must be a whole number.", translationFunction = defaultTFunction, id = 'wholeNumberOnly' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorFn: (value) => !value || Number.isInteger(Number(value)),
        id
    };
}
exports.wholeNumberOnly = wholeNumberOnly;
/**
 * Best used as validation for combobox(), currencyField(), datepicker(), inputField(), select(), textArea(), toggle()
 *
 * Note: Not recommended to be used as validation with radioGroup as radio buttons should always have one of the radios selected.
 *
 * required() === true for values: "0", "100", "abc", etc.
 * required() === false for values: "", undefined, null, etc.
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param options
 */
function required(options = {}) {
    const { message = "This field is required.", translationFunction = defaultTFunction, id = 'required' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.REQUIRED,
        value: true,
        id
    };
}
exports.required = required;
/**
 * A helper function that can be used to create a custom validation for any field. It is recommended to have
 * this custom validation return true if no input is entered. Use required() to validate a value must be entered.
 *
 * @param validatorReference
 * @param validatorFn
 * @param options
 */
function customValidation(validatorReference, validatorFn, options = {}) {
    const { message = `This field does not pass the ${validatorReference} validator`, translationFunction = defaultTFunction, id = 'customValidation' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.CUSTOM,
        value: undefined,
        validatorReference,
        validatorFn,
        id
    };
}
exports.customValidation = customValidation;
/**
 * Best used as validation for combobox(), currencyField(), inputField(), textArea()
 *
 * Takes in a regex pattern that the entered value is matched against.
 *
 * no input will pass validation, ie "". Use required() to validate a value must be entered.
 *
 * @param regex
 * @param options
 */
function pattern(regex, options = {}) {
    const { message = 'This field does not satisfy the pattern constraint', translationFunction = defaultTFunction, id = 'pattern' } = options;
    return {
        message: translationFunction(message),
        type: enums_1.FluidFieldValidationType.PATTERN,
        value: regex,
        id
    };
}
exports.pattern = pattern;
